<template>
  <div
    class="form__group"
    :class="{
      'form__group--completed-icon': useCompletedIcon,
      'form__group--error': hasError,
      'form__group--warning': warning,
    }"
  >
    <CInput
      :id="id"
      :class="{ [`c-input--icon-${iconLocation}`]: iconLocation }"
      :error="error"
      :type="inputType"
      :disabled="disabled"
      :autofocus="autofocus"
      :autocomplete="autocomplete"
      :value="value"
      :name="name"
      :inputmode="inputmode"
      :pattern="pattern"
      :required="required"
      :maxlength="maxlength"
      :minlength="minlength"
      :placeholder-label="placeholder"
      @update:model-value="(value: string) => $emit('update:model-value', value)"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
      @keydown="$emit('keydown', $event)"
      @paste="$emit('paste', $event)"
    />
    <CIcon
      v-if="iconName"
      class="text-gray-800"
      :class="{
        [hasError || warning
          ? `c-icon--2${iconLocation}`
          : `c-icon--${iconLocation}`]: iconLocation,
      }"
      :name="iconName"
    />
    <CIcon
      v-if="hasError || warning"
      class="c-icon--right"
      :class="hasError ? 'text-red-500' : 'text-orange-500'"
      name="warning"
    />
    <CIcon
      v-else-if="useCompletedIcon && value && (value as string).length > 0"
      name="check"
    />

    <button
      v-if="type === 'password' && showPasswordToggle"
      type="button"
      class="c-toggle-button-input-group"
      :class="{ 'c-toggle-button-input-group--left': hasError || warning }"
      role="switch"
      :aria-checked="showPassword"
      @click="toggleShowPassword"
    >
      <CIcon
        :name="showPassword ? 'visibility_off' : 'visibility'"
        :title="passwordToggleText"
      />
    </button>

    <label
      v-if="type !== 'hidden'"
      class="placeholder"
      :class="{ [`placeholder--${iconLocation}`]: iconName }"
    >
      <span
        v-if="required"
        class="placeholder--required"
      >
        *
      </span>
      {{ placeholder }}
    </label>
    <div
      v-if="errorMessage || helperText"
      :class="[errorMessage ? 'form__error' : 'form__helper-text']"
    >
      <span>{{ errorMessage ? errorMessage : helperText }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import CInput from '../../atoms/CInput'
import CIcon from '../../atoms/CIcon'

interface Props {
  type?: 'text' | 'email' | 'url' | 'tel' | 'search' | 'password' | 'hidden'
  maxlength?: number
  minlength?: number
  value?: string
  placeholder?: string
  errorMessage?: string
  name?: string
  useCompletedIcon?: boolean
  error?: boolean
  pattern?: string
  inputmode?: 'search' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal'
  required?: boolean
  autocomplete?: string
  disabled?: boolean
  autofocus?: boolean
  id?: string
  iconLocation?: 'left' | 'right'
  iconName?: string
  helperText?: string
  warning?: boolean
  showPasswordToggle?: boolean
  passwordToggleText?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  maxlength: undefined,
  minlength: undefined,
  value: '',
  placeholder: '',
  errorMessage: '',
  name: '',
  useCompletedIcon: false,
  error: false,
  pattern: undefined,
  inputmode: undefined,
  required: false,
  autocomplete: undefined,
  disabled: false,
  id: undefined,
  iconLocation: 'right',
  iconName: '',
  helperText: '',
  warning: false,
  showPasswordToggle: false,
  passwordToggleText: undefined,
})

defineEmits<{
  (e: 'update:model-value', value: string): void
  (e: 'focus', event: FocusEvent): void
  (e: 'blur', event: FocusEvent): void
  (e: 'keydown', event: KeyboardEvent): void
  (e: 'paste', event: ClipboardEvent): void
}>()

const showPassword = ref(false)

const hasError = computed(() => props.error || props.errorMessage)

const inputType = computed(() => {
  if (
    props.type === 'password' &&
    props.showPasswordToggle &&
    showPassword.value
  ) {
    return 'text'
  }
  return props.type
})

const toggleShowPassword = () => {
  showPassword.value = !showPassword.value
}
</script>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CInputGroup',
})
</script>
